import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { announcementsService } from '../services/api';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill/dist/quill.snow.css';
import { Icon } from '@iconify/react/dist/iconify.js';

const AnnouncementAddPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info',
    priority: 0,
    show_as_popup: true,
    start_date: '',
    end_date: '',
    send_notification: false
  });

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (isEdit && id) {
      loadAnnouncement();
    }
  }, [isEdit, id]);

  const loadAnnouncement = async () => {
    try {
      setLoadingData(true);
      const response = await announcementsService.getAnnouncement(id);
      if (response.success) {
        const announcement = response.data;
        setFormData({
          title: announcement.title || '',
          content: announcement.content || '',
          type: announcement.type || 'info',
          priority: announcement.priority || 0,
          show_as_popup: announcement.show_as_popup || false,
          start_date: announcement.start_date ? announcement.start_date.split('T')[0] : '',
          end_date: announcement.end_date ? announcement.end_date.split('T')[0] : '',
          send_notification: false // Edit'te notification göndermiyoruz
        });
      } else {
        toast.error(response.error || 'Duyuru yüklenemedi');
        navigate('/announcements');
      }
    } catch (error) {
      console.error('Error loading announcement:', error);
      toast.error('Duyuru yüklenirken bir hata oluştu');
      navigate('/announcements');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Başlık gereklidir');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('İçerik gereklidir');
      return;
    }

    try {
      setLoading(true);
      let response;

      if (isEdit) {
        response = await announcementsService.updateAnnouncement(id, {
          title: formData.title,
          content: formData.content,
          type: formData.type,
          priority: formData.priority,
          show_as_popup: formData.show_as_popup,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null
        });
      } else {
        response = await announcementsService.createAnnouncement({
          title: formData.title,
          content: formData.content,
          type: formData.type,
          priority: formData.priority,
          show_as_popup: formData.show_as_popup,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          send_notification: formData.send_notification
        });
      }

      if (response.success) {
        toast.success(isEdit ? 'Duyuru başarıyla güncellendi' : 'Duyuru başarıyla oluşturuldu');
        navigate('/announcements');
      } else {
        toast.error(response.error || (isEdit ? 'Duyuru güncellenemedi' : 'Duyuru oluşturulamadı'));
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error(isEdit ? 'Duyuru güncellenirken bir hata oluştu' : 'Duyuru oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image',
    'color', 'background',
    'align'
  ];

  if (loadingData) {
    return (
      <MasterLayout>
        <Breadcrumb title={isEdit ? "Duyuru Düzenle" : "Duyuru Ekle"} />
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb title={isEdit ? "Duyuru Düzenle" : "Duyuru Ekle"} />
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">{isEdit ? "Duyuru Düzenle" : "Duyuru Ekle"}</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Başlık */}
            <div className="mb-4">
              <label className="form-label">Başlık <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                placeholder="Duyuru başlığı"
              />
            </div>

            {/* İçerik - Zengin Metin Editörü */}
            <div className="mb-4">
              <label className="form-label">İçerik <span className="text-danger">*</span></label>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                modules={quillModules}
                formats={quillFormats}
                style={{ minHeight: '300px' }}
              />
            </div>

            {/* Tip ve Öncelik */}
            <div className="row mb-4">
              <div className="col-md-6">
                <label className="form-label">Tip</label>
                <select
                  className="form-select"
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                >
                  <option value="info">Bilgi</option>
                  <option value="success">Başarı</option>
                  <option value="warning">Uyarı</option>
                  <option value="error">Hata</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Öncelik</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  min="0"
                  placeholder="0"
                />
                <small className="text-muted">Yüksek öncelik önce gösterilir</small>
              </div>
            </div>

            {/* Popup ve Notification */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="show_as_popup"
                    checked={formData.show_as_popup}
                    onChange={(e) => setFormData(prev => ({ ...prev, show_as_popup: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="show_as_popup">
                    Popup olarak göster
                  </label>
                </div>
              </div>
              {!isEdit && (
                <div className="col-md-6">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="send_notification"
                      checked={formData.send_notification}
                      onChange={(e) => setFormData(prev => ({ ...prev, send_notification: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="send_notification">
                      Partner'lere bildirim gönder
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Tarihler */}
            <div className="row mb-4">
              <div className="col-md-6">
                <label className="form-label">Başlangıç Tarihi (Opsiyonel)</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Bitiş Tarihi (Opsiyonel)</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Butonlar */}
            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {isEdit ? 'Güncelleniyor...' : 'Oluşturuluyor...'}
                  </>
                ) : (
                  <>
                    <Icon icon="solar:check-circle-outline" className="me-2" />
                    {isEdit ? 'Güncelle' : 'Oluştur'}
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/announcements')}
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      </div>
    </MasterLayout>
  );
};

export default AnnouncementAddPage;

